import React, { useContext, useState, useEffect } from "react";

import { Modal, Stack, Typography } from "@mui/material";
import moment from "moment";
import useAxios from "axios-hooks";

import { useCapturePayments } from "hooks/useCapturePayments";
import { useQuickBookOrder } from "hooks/useQuickBookOrder";

import Loader from "components/loader";
import MainCard from "components/mainCard";
import TextField from "components/textField";
import Button from "components/button";
import SearchableDropDown from "components/dropdowns/searchableDropDown";
import { OrderTimelineProp } from "../../orderStatusAction";

import { OptionSetContext } from "features/components/optionSetContext";
import { actionModalSX } from "features/orders/index.styles";
import { textAreaStyles } from "features/products/index.styles";
import {
  getFormDataPayloadAsJson,
  getLocalStorage,
} from "features/components/helperFunctions";
import { API_END_POINTS } from "utils/constants/APIEndPoints";
import { DropDownList, OrderDetailTypes } from "utils/types";
import {
  verifyBamboraPreAuthStatus,
  verifyGlobalPaymentPreAuthStatus,
  verifyQuickBookInventoryStatus,
} from "utils/helperFunctions";
import { orderStatus, paymentMethods, source } from "utils/constants";
import { logDataChange } from "services/fireStore";
import { dispatch, useSelector } from "store";
import { openSnackbar } from "store/slices/snackbar";

interface errorProps {
  commentError: string;
  shippingError: string;
}

const OrderStatusActionButton = ({
  color,
  status,
  user_email,
  order_id,
  orderType,
  quickBookInvoice,
  refetchTimelineCallback,
  loadingTimeline,
}: OrderTimelineProp) => {
  // Local States
  const [currentStatus, setCurrentStatus] = useState(status);

  const [toggleStatusModal, setToggleStatusModal] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [error, setError] = useState<errorProps>({
    commentError: "",
    shippingError: "",
  });

  // Global States
  const {
    setLoaders,
    loaders,
    selectedOrderContext,
    setPreventOrderUpdate,
    cancelUpdateButtonToggle,
  } = useContext(OptionSetContext);
  const {
    paymentSettings,
    delivery_services: deliveryServices,
    // third_party_inventory: thirdPartyInventory,
  } = useSelector((state) => state.global);

  const [selectDeliveryService, setSelectDeliveryService] =
    useState<DropDownList>({
      label: "",
      value: "",
    });
  const [shipping, setShipping] = useState<DropDownList>({
    label: "Select Shipping",
    value: "select_shipping",
  });
  const [deliveryOptions, setDeliveryOptions] = useState<DropDownList[]>([
      { label: "", value: "" },
    ]),
    [preparationTime, setPreparationTime] = useState<string>("");

  const { globalSettings } = useSelector((state) => state.global);

  const { eatout_id, user_id } = getLocalStorage();

  //======================================= API Payloads =======================================//

  const preAuthPaymentAPIPayload = (item: OrderDetailTypes) => {
    const formData = new FormData();

    // using Iqbal for now
    if (item?.transaction_id) {
      formData.append("transaction_id", `${item["transaction_id"]}`);
    }

    // set payment type and action
    if (item?.paymentType) {
      formData.append("payment_type", item?.paymentType);
      formData.append("order_action", "capture");
    }

    formData.append("comment", comment);
    formData.append("business_id", `${item["business_id"]}`);
    formData.append("amount", `${item["amount"]}`);
    formData.append("order_id", `${item["order_id"]}`);
    formData.append("admin_id", `${item["admin_id"]}`);
    formData.append("source", "biz");

    return formData;
  };

  // order status update API call payload
  const orderStatusUpdateAPIPayload = (tracking_id?: string) => {
    const { order_id } = selectedOrderContext;

    const formData = new FormData();

    formData.append("eatout_id", `${eatout_id}`);
    formData.append("order_id", order_id);
    formData.append("status", currentStatus);
    formData.append("eatout_uid", `${user_id}`);
    formData.append(
      "comment",
      selectDeliveryService.value !== "self_deliver"
        ? tracking_id
          ? tracking_id
          : comment
          ? comment
          : ""
        : comment
        ? comment
        : ""
    );
    formData.append("admin_id", `${user_id}`);
    formData.append("source", "biz");
    return formData;
  };

  // Delivery Service API
  const deliveryServiceAPIPayload = () => {
    const formData = new FormData();
    let appendPrepTime: any = { ...selectedOrderContext };

    if (selectDeliveryService.value === "doordash") {
      //   // This is intentionally commented required in near future
      // const getMinutes = convertTimeToMinutes(preparationTime);

      // Convert the given time to UTC
      const utcDateTimeString = moment()
        .add(preparationTime, "minutes")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      appendPrepTime = {
        ...appendPrepTime,
        pickup_time: utcDateTimeString,
        prep_time: preparationTime,
      };
    }

    formData.append("business_id", `${eatout_id}`);
    formData.append("order_data", JSON.stringify(selectedOrderContext));
    formData.append("comment", comment);
    formData.append("action", selectDeliveryService.value);
    formData.append("delivery_type", `${1}`);
    formData.append("admin_id", `${user_id}`);
    formData.append("source", "biz");
    return formData;
  };

  // get order options API call payload
  const getOrderOptionsAPIPayload = () => {
    const formData = new FormData();

    formData.append("eatout_id", `${eatout_id}`);
    formData.append("admin_id", `${user_id}`);
    formData.append("source", "biz");

    return formData;
  };

  // const inventoryUpdateAPIPayload = () => {
  //   const { order_detail } = selectedOrderContext;
  //   const productDetails = order_detail.map(
  //     (item: OrderListingResponseOrderDetail) => ({
  //       item_id: item.item_id,
  //       product_id: item.product_code,
  //       qty: item.quantity,
  //     })
  //   );

  //   return JSON.stringify({
  //     business_id: eatout_id,
  //     product_details: productDetails,
  //   });
  // };

  // const placeOrderToDistributorAPIPayload = () => {
  //   const { order_detail } = selectedOrderContext;
  //   const productDetails = order_detail.map(
  //     (item: OrderListingResponseOrderDetail) => ({
  //       item_id: item.item_id,
  //       product_id: item.product_code,
  //       qty: item.quantity,
  //     })
  //   );

  //   return JSON.stringify({
  //     location_id: selectDeliveryService.value,
  //     business_id: eatout_id,
  //     order_details: productDetails,
  //   });
  // };

  //======================================= API Calls =======================================//

  // API call to get order options
  const [
    {
      data: getOrderOptionsData,
      loading: getOrderOptionsLoading,
      error: getOrderOptionsError,
    },
    getOrderOptionsAPI,
  ] = useAxios(
    {
      ...API_END_POINTS.getOrderOptions,
    },
    { manual: true }
  );

  // Pre-auth - Global Settings payment status 200
  const [{ loading: paymentLoading }, preAuthPaymentAPICall] = useAxios(
    {
      url: verifyBamboraPreAuthStatus(globalSettings)
        ? API_END_POINTS?.bamboraPayment
        : API_END_POINTS?.stripePayment,
      method: "post",
      headers: { "Content-Type": "multipart/form-data" },
    },
    { manual: true }
  );

  // order status update API call
  const [{ loading: statusUpdateLoading }, orderStatusUpdateAPICall] = useAxios(
    {
      url: "/order_status_update",
      method: "post",
    },
    { manual: true }
  );

  const [{}, deliveryServiceAPI] = useAxios(
    {
      url: "/thirdparty_delivery_service",
      method: "POST",
    },
    { manual: true }
  );

  // const [{}, thirdPartyInventoryUpdateAPI] = useAxios(
  //   API_END_POINTS.thirdPartyInventoryFetchAll,
  //   { manual: true }
  // );

  // const [{}, placeOrderToDistributorAPI] = useAxios(
  //   API_END_POINTS.thirdPartyInventoryPlaceOrder,
  //   { manual: true }
  // );

  // ==============================|| custom hooks ||============================== //

  // custom hook to call capture third party payments API
  const { getPaymentCaptureLoading, paymentCaptureAPICall } =
    useCapturePayments();

  // custom hook to call create/cancel quickBook order API
  const { quickBookOrderLoading, quickBookOrderAPICall } = useQuickBookOrder();

  //======================================= Side Effects =======================================//

  useEffect(() => {
    // if (toggleStatusModal && thirdPartyInventory.status === "1") {
    //   thridPartyInventoryUpdate();
    // }

    if (toggleStatusModal) {
      getOrderOptionsAPICall();
    }
  }, [toggleStatusModal]);

  useEffect(() => {
    const whichLoader =
      paymentLoading || statusUpdateLoading || loadingTimeline;

    if (whichLoader) {
      setLoaders(whichLoader);
    } else if (!whichLoader) {
      setLoaders(false);
    }
  }, [paymentLoading || statusUpdateLoading || loadingTimeline]);

  //======================================= Handlers =======================================//

  // const thridPartyInventoryUpdate = async () => {
  //   const result = await thirdPartyInventoryUpdateAPI({
  //     data: inventoryUpdateAPIPayload(),
  //   });

  //   if (result.data.status == 200 || result.data.status === 201) {
  //     const distributorList = result?.data?.dc_centers.map(
  //       (distributorCenter: { name: String; location_id: number }) => ({
  //         label: distributorCenter.name,
  //         value: distributorCenter.location_id,
  //       })
  //     );
  //     // success response
  //     setDeliveryOptions(distributorList);
  //   } else if (result?.data?.status == 400 || result?.data?.status == 404) {
  //     // failure response
  //     setNotifyMessage(result.data.message);
  //     setNotifyType("error");
  //     setNotify(true);
  //   }
  // };

  // select distributers
  // const placeOrderToDistributor = async () => {
  //   const result = await placeOrderToDistributorAPI({
  //     data: placeOrderToDistributorAPIPayload(),
  //   });

  //   if (result.data.status == 200 || result.data.status === 201) {
  //     updateOrderStatus();
  //   } else {
  //     // failure response
  //     setNotifyMessage("Some thing Went wrong");
  //     setNotifyType("error");
  //     setNotify(true);
  //   }
  // };

  const getOrderOptionsAPICall = async () => {
    const result = await getOrderOptionsAPI({
      data: getOrderOptionsAPIPayload(),
    });

    if (result?.status == 200) {
      // success response
      setPreparationTime(`${result?.data?.result?.preparation_time}`);
    } else {
      dispatch(
        openSnackbar({
          open: true,
          message: result.data.message,
          alert: { color: "error" },
        })
      );
    }
  };

  const deliveryService = async () => {
    const result = await deliveryServiceAPI({
      data: deliveryServiceAPIPayload(),
    });

    if (result.data.status == 200 || result.data.status === 201) {
      // success response
      if (result.data.tracking_id) {
        // bykea case
        await updateOrderStatus(result.data.tracking_id);
      } else if (result.data.hasOwnProperty("doordash_delivery_id")) {
        // door dash case
        await updateOrderStatus();
      }
    } else if (result.data.status == 400 || result.data.status == 404) {
      dispatch(
        openSnackbar({
          open: true,
          message: result.data.message,
          alert: { color: "error" },
        })
      );
    }
  };
  function returnMessageAndUrl() {
    return {
      url: verifyBamboraPreAuthStatus(globalSettings)
        ? API_END_POINTS?.bamboraPayment
        : paymentSettings.preauth && paymentSettings.status === 200
        ? API_END_POINTS?.stripePayment
        : API_END_POINTS?.bulkStatusUpdate?.url,
      message: verifyBamboraPreAuthStatus(globalSettings)
        ? "Payment Captured - Error"
        : paymentSettings.preauth && paymentSettings.status === 200
        ? "Payment Captured - Tezmart"
        : "Order Status Error",
    };
  }
  function returnPayloads(grand_total: any, tracking_id?: string) {
    return {
      payload: verifyBamboraPreAuthStatus(globalSettings)
        ? getFormDataPayloadAsJson(
            preAuthPaymentAPIPayload({
              business_id: Number(eatout_id) || 0,
              transaction_id: selectedOrderContext.pre_auth_trans[0]?.trans_id,
              amount: grand_total,
              order_id: selectedOrderContext.pre_auth_trans[0]?.orderid,
              user_email: user_email,
              admin_id: Number(user_id) || 0,
              source: "biz",
            })
          )
        : paymentSettings.preauth && paymentSettings.status === 200
        ? getFormDataPayloadAsJson(
            preAuthPaymentAPIPayload({
              business_id: Number(eatout_id) || 0,
              amount: grand_total,
              order_id: selectedOrderContext.pre_auth_trans[0]?.orderid,
              user_email: user_email, // TODO: Remove email and other extra things
              admin_id: Number(user_id) || 0,
              source: "biz",
            })
          )
        : getFormDataPayloadAsJson(orderStatusUpdateAPIPayload(tracking_id)),
    };
  }

  const updateOrderStatus = async (tracking_id?: string) => {
    const { pre_auth, grand_total } = selectedOrderContext;

    let result: any = "";
    try {
      if (currentStatus === "Confirmed" && Number(pre_auth)) {
        if (
          parseFloat(grand_total) >
          parseFloat(selectedOrderContext.pre_auth_trans[0]?.trans_amount)
        ) {
          dispatch(
            openSnackbar({
              open: true,
              message: "Pre-authorized amount is less than grand total.",
              alert: { color: "error" },
            })
          );
          return;
        }

        // tezmart
        if (paymentSettings.preauth && paymentSettings.status === 200) {
          result = await preAuthPaymentAPICall({
            data: preAuthPaymentAPIPayload({
              business_id: Number(eatout_id) || 0,
              amount: grand_total,
              order_id: selectedOrderContext.pre_auth_trans[0]?.orderid,
              user_email: user_email, // TODO: Remove email and other extra things
              admin_id: Number(user_id) || 0,
              source: "biz",
            }),
          });
        } else if (verifyBamboraPreAuthStatus(globalSettings)) {
          logDataChange({
            action: {
              url: API_END_POINTS?.bamboraPayment,
              method: "POST",
              payload: getFormDataPayloadAsJson(
                preAuthPaymentAPIPayload({
                  business_id: Number(eatout_id) || 0,
                  transaction_id:
                    selectedOrderContext.pre_auth_trans[0]?.trans_id,
                  amount: grand_total,
                  order_id: selectedOrderContext.pre_auth_trans[0]?.orderid,
                  user_email: user_email,
                  admin_id: Number(user_id) || 0,
                  source: "biz",
                })
              ),
              response: "",
            },
            message: "Payment Captured - Request",
          });

          result = await preAuthPaymentAPICall({
            data: preAuthPaymentAPIPayload({
              business_id: Number(eatout_id) || 0,
              transaction_id: selectedOrderContext.pre_auth_trans[0]?.trans_id,
              amount: grand_total,
              order_id: selectedOrderContext.pre_auth_trans[0]?.orderid,
              user_email: user_email, // TODO: Remove email and other extra things
              admin_id: Number(user_id) || 0,
              source: "biz",
            }),
          });

          // log payment captured
          logDataChange({
            action: {
              url: API_END_POINTS?.bamboraPayment,
              method: "POST",
              payload: getFormDataPayloadAsJson(
                preAuthPaymentAPIPayload({
                  business_id: Number(eatout_id) || 0,
                  transaction_id:
                    selectedOrderContext.pre_auth_trans[0]?.trans_id,
                  amount: grand_total,
                  order_id: selectedOrderContext.pre_auth_trans[0]?.orderid,
                  user_email: user_email,
                  admin_id: Number(user_id) || 0,
                  source: "biz",
                })
              ),
              response: JSON.stringify(result?.data),
            },
            message: "Payment Captured - Response",
          });
        } else if (verifyGlobalPaymentPreAuthStatus(globalSettings)) {
          result = await paymentCaptureAPICall({
            data: preAuthPaymentAPIPayload({
              business_id: Number(eatout_id) || 0,
              amount: grand_total,
              order_id: selectedOrderContext.pre_auth_trans[0]?.orderid,
              user_email: user_email, // TODO: Remove email and other extra things
              admin_id: Number(user_id) || 0,
              paymentType: paymentMethods?.GLOBAL,
              source,
            }),
          });
        }
      } else {
        // quickBook third party inventory case
        if (verifyQuickBookInventoryStatus(currentStatus, globalSettings)) {
          // function call to create/cancel order in quickBook inventory
          const quickBookResponse: string = await callQuickBookInventoryAPI();

          // return if no invoice found
          if (!quickBookResponse) {
            return;
          }
        }

        // update order status API call
        result = await orderStatusUpdateAPICall({
          data: orderStatusUpdateAPIPayload(tracking_id),
        });
      }

      const getResultsFirst = async () => {
        // if no result found
        if (!result || !result.data) return;

        const {
          data: { status, message, message_id },
        } = result;
        const { data } = result;

        if (status === 400 || status == 0) {
          dispatch(
            openSnackbar({
              open: true,
              message: message_id || message,
              alert: { color: "error" },
            })
          );

          // log error here
          logDataChange({
            action: {
              url: returnMessageAndUrl().url,
              method: "POST",
              payload: returnPayloads(selectedOrderContext?.grand_total)
                .payload,
              response: JSON.stringify(data),
            },
            message: returnMessageAndUrl().message,
          });
        } else if (status && (status === 200 || status == 1)) {
          dispatch(
            openSnackbar({
              open: true,
              message: message,
              alert: { color: "success" },
            })
          );

          if (
            currentStatus.toLowerCase() !== "pending" &&
            currentStatus.toLowerCase() !== "viewed"
          ) {
            setPreventOrderUpdate(true);
          }
        }
      };
      await getResultsFirst();

      await refetchTimelineCallback();
    } catch (err) {
      // log payment captured
      logDataChange({
        action: {
          url: returnMessageAndUrl().url,
          method: "POST",
          payload: returnPayloads(selectedOrderContext?.grand_total).payload,
          response: JSON.stringify(err),
        },
        message: returnMessageAndUrl().message,
      });

      refetchTimelineCallback();
    }
  };

  // function to call create/cancel order in quickBook inventory
  const callQuickBookInventoryAPI = async (): Promise<string> => {
    let quickBookAPIResponse: string = "";

    // check if order status is confirmed or cancel only
    if (
      currentStatus === orderStatus?.CONFIRMED ||
      currentStatus === orderStatus?.CANCEL
    ) {
      // API call to create/cancel quickBook order
      quickBookAPIResponse = await quickBookOrderAPICall(
        currentStatus === orderStatus?.CONFIRMED
          ? order_id?.toString()!
          : quickBookInvoice || "0",
        currentStatus === orderStatus?.CONFIRMED
      );
    }

    return quickBookAPIResponse;
  };

  const handleComment = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setComment(e.target.value);
    setError({ commentError: "", shippingError: "" });
  };

  const toggleOrderStatusModal = () => {
    setToggleStatusModal((prevModalState) => !prevModalState);
    setError({ commentError: "", shippingError: "" });
  };

  const changeOrderStatus = async () => {
    if (currentStatus === "Cancel" && !comment) {
      setError({ commentError: "Required*", shippingError: "" });
      return;
    } else if (
      selectShipping() &&
      // thirdPartyInventory.status === "0" &&
      selectDeliveryService.value === "tcs_logistics"
    ) {
      setError({ commentError: "", shippingError: "" });
      // handle tcs shipping case
      deliveryService();
    } else if (
      selectDeliveryService.value &&
      // thirdPartyInventory.status === "0" &&
      selectDeliveryService.value !== "tcs_logistics" &&
      selectDeliveryService.value !== "self_deliver"
    ) {
      deliveryService();
    }
    // if (
    //   selectDeliveryService.value &&
    //   thirdPartyInventory.status === "1"
    // ) {
    //   // ez eats case, dropdown selection case
    //   placeOrderToDistributor();
    // } else if (
    //   thirdPartyInventory.status === "0" &&
    //   currentStatus !== thirdPartyInventory.order_status
    // ) {
    //   // simple order status change case
    //   updateOrderStatus();
    // } else if (
    //   thirdPartyInventory.status === "1" &&
    //   currentStatus !== thirdPartyInventory.order_status
    // )
    else {
      // ez eat normal status update case
      updateOrderStatus();
    }
    toggleOrderStatusModal();
  };

  const selectShipping = () => {
    if (shipping.value == "select_shipping" || !shipping) {
      setError({
        commentError: "",
        shippingError: "Please Select Shipping Type",
      });
      return false;
    }
    return true;
  };

  const handleDeliverService = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      setSelectDeliveryService(value);
    } else {
      setSelectDeliveryService({
        label: "",
        value: "",
      });
    }

    setError({ commentError: "", shippingError: "" });
  };

  const handlePrepTimeChange = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setPreparationTime(e.target.value);
  };

  const handleShipping = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      setShipping(value);
    } else {
      setShipping({ label: "", value: "" });
    }

    setError({ commentError: "", shippingError: "" });
  };

  const darken = (
    hexColor: string | undefined,
    percent: number = 10
  ): string | undefined => {
    if (!hexColor) {
      return undefined;
    }

    const darkenFactor = 100 - percent;
    return `#${hexColor
      .slice(1)
      .match(/.{2}/g)
      ?.map((color) =>
        Math.round((parseInt(color, 16) * darkenFactor) / 100)
          .toString(16)
          .padStart(2, "0")
      )
      ?.join("")}`;
  };

  const returnColorHex = () => {
    const color = {
      fontFamily: "Roboto",
      fontStyle: "normal",
      fontWeight: "500",
      fontSize: "13px",
      lineHeight: "15px",
      background: "",
      color: "",
      hover: "",
    };

    switch (currentStatus) {
      case "Pending":
        color.background = "rgb(0 0 0 / 26%)";
        color.color = "#ffffff";
        color.hover = darken("rgb(0 0 0 / 26%)", 10) || "";
        break;

      case "Confirmed":
        color.background = "#C7FFD6";
        color.color = "#00C853";
        color.hover = darken("#C7FFD6", 10) || "";
        break;

      case "Cancelled":
      case "Cancel":
        color.background = "#FBE9E7";
        color.color = "#D84315";
        color.hover = darken("#FBE9E7", 10) || "";
        break;

      case "Shipped":
      case "Viewed":
      case "Prepared":
      case "Dispatched":
      case "Delivered":
        color.background = "#E3F2FD";
        color.color = "#2196F3";
        color.hover = darken("#E3F2FD", 10) || "";
        break;

      default:
        color.background = "#FFF4D0";
        color.color = "#EEB304";
        color.hover = darken("#FFF4D0", 10) || "";
        break;
    }

    return {
      color: color.color,
      background: color.background,
      hover: color.hover,
    };
  };

  return (
    <>
      {(statusUpdateLoading ||
        getPaymentCaptureLoading ||
        quickBookOrderLoading) && <Loader />}

      <Button
        disabled={loaders || cancelUpdateButtonToggle}
        sx={{
          backgroundColor:
            loaders || cancelUpdateButtonToggle
              ? "#bcb8b9"
              : returnColorHex().background,
          width: "80px",
          height: " 30px",
          borderRadius: "100px",
          boxShadow:
            "0 5px 15px rgba(0,0,0,.05), 0 4px 10px rgba(33,37,41,.25)",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            backgroundColor: returnColorHex().hover,
            boxShadow:
              "0 8px 20px rgba(0,0,0,.1), 0 6px 15px rgba(33,37,41,.35)",
          },
        }}
        onClick={toggleOrderStatusModal}
      >
        <Typography
          sx={{
            color:
              currentStatus === "Pending"
                ? "rgb(0 0 0 / 50%)"
                : loaders || cancelUpdateButtonToggle
                ? "#673AB7"
                : returnColorHex().color,
          }}
        >
          {currentStatus === "Confirmed" ? "Confirm" : currentStatus}
        </Typography>
      </Button>

      <Modal // order status change modal
        open={toggleStatusModal}
        onClose={toggleOrderStatusModal}
      >
        <MainCard
          contentSX={{
            "& .MuiCardContent-root": {
              p: "unset !important",
            },
            m: "unset",
            p: "unset !important",
          }}
          sx={actionModalSX}
        >
          <Stack>
            <Stack>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontStyle: "normal",
                  fontWeight: 700,
                  fontSize: "20px",
                  color: "#212121",
                }}
              >
                {currentStatus && currentStatus === "Cancel"
                  ? "Are you sure you want to cancel the order?"
                  : `Are you sure you want to change the status to ${
                      currentStatus === "Confirmed" ? "Confirm" : currentStatus
                    }?`}
              </Typography>
            </Stack>

            <Stack>
              {/* show text only in Cancel case */}
              {currentStatus && currentStatus === "Cancel" && (
                <Stack mt="10px">
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontStyle: "normal",
                      fontWeight: 400,
                      fontSize: "14px",
                      color: "#616161",
                    }}
                  >
                    Order placed by the customer will be cancel, you cannot undo
                    the action.
                  </Typography>
                </Stack>
              )}

              {/* visible to delivery order types & having delivery service */}
              {orderType === "0" &&
                currentStatus === deliveryServices.order_status &&
                deliveryServices.status === "1" && (
                  <>
                    <Stack mt="40px">
                      <SearchableDropDown
                        name="selectDeliveryService"
                        label="Select Delivery Service"
                        handleChange={handleDeliverService}
                        value={selectDeliveryService}
                        options={[
                          ...deliveryOptions,
                          { label: "Self Deliver", value: "self_deliver" },
                          {
                            label: deliveryServices.service_name,
                            value: deliveryServices.service_action,
                          },
                        ]}
                      />
                    </Stack>
                    {selectDeliveryService.value === "doordash" && (
                      <Stack mt="20px" mb="0px !important">
                        <TextField
                          label="Preparation Time"
                          name="preptime"
                          value={preparationTime}
                          onChange={handlePrepTimeChange}
                          type="number"
                          sx={textAreaStyles}
                        />
                      </Stack>
                    )}
                  </>
                )}

              {/* disctributor list */}
              {/* {currentStatus === thirdPartyInventory.order_status &&
                thirdPartyInventory.status === "1" && (
                  <Stack mt="40px">
                    <SearchableDropDown
                      name="selectDeliveryService"
                      label="Distributors List"
                      handleChange={handleDeliverService}
                      value={selectDeliveryService}
                      options={deliveryOptions}
                    />
                  </Stack>
                )} */}

              {selectDeliveryService.value == "tcs_logistics" && (
                <Stack mt="20px">
                  <SearchableDropDown
                    name="shipping"
                    label="Select Shipping"
                    handleChange={handleShipping}
                    value={shipping}
                    options={[
                      { label: "One Day Delivery", value: "0" },
                      { label: "Two Days Delivery", value: "1" },
                    ]}
                    isError={error.shippingError !== "" ? true : false}
                    helperText={error.shippingError}
                  />
                </Stack>
              )}

              <Stack
                mt={selectDeliveryService.value === "doordash" ? "0" : "20px"}
              >
                <TextField
                  label="Reason/comment"
                  name="Reason/comment"
                  value={comment}
                  onChange={handleComment}
                  error={error.commentError === "" ? false : true}
                  helperText={error.commentError}
                  rows={2}
                  multiline={true}
                  type="text"
                  sx={textAreaStyles}
                />
              </Stack>
            </Stack>

            <Stack direction="row" sx={{ justifyContent: "end" }} spacing={1.5}>
              <Button
                color={"secondary"}
                variant={"contained"}
                sx={{
                  background: "#F5F5F5 ",
                  color: "#212121",
                  p: "12px 57px",

                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                  },
                }}
                onClick={toggleOrderStatusModal}
              >
                No
              </Button>

              <Button
                color={"secondary"}
                variant={"contained"}
                sx={{ p: "12px 30px" }}
                onClick={changeOrderStatus}
              >
                Yes, {currentStatus === "Confirmed" ? "Confirm" : currentStatus}
              </Button>
            </Stack>
          </Stack>
        </MainCard>
      </Modal>
    </>
  );
};

export default OrderStatusActionButton;
